import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import blacklistService from '../services/blacklist.service';

const Blacklist = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  
  const [searchForm, setSearchForm] = useState({
    lastName: '',
    firstName: '',
    middleName: ''
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  const [addForm, setAddForm] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    birthDate: '',
    comment: ''
  });

  const [editForm, setEditForm] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    birthDate: '',
    comment: ''
  });

  const handleSearchChange = (e) => {
    setSearchForm({
      ...searchForm,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    fetchSearchHistory();
  }, []);

  const fetchSearchHistory = async () => {
    try {
      const result = await blacklistService.getSearchHistory(10);
      if (result.success) {
        setSearchHistory(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
    }
  };

  const loadFromHistory = (historyItem) => {
    setSearchForm({
      lastName: historyItem.lastName,
      firstName: historyItem.firstName || '',
      middleName: historyItem.middleName || ''
    });
  };

  const clearHistory = async () => {
    try {
      const result = await blacklistService.clearSearchHistory();
      if (result.success) {
        setSearchHistory([]);
        enqueueSnackbar('История поиска очищена', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Ошибка при очистке истории', { variant: 'error' });
    }
  };

  const handleAddChange = (e) => {
    setAddForm({
      ...addForm,
      [e.target.name]: e.target.value
    });
  };

  const handleEditChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = async () => {
    if (!searchForm.lastName.trim()) {
      enqueueSnackbar('Фамилия обязательна для поиска', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const result = await blacklistService.searchBlacklist(
        searchForm.lastName,
        searchForm.firstName,
        searchForm.middleName
      );

      if (result.success) {
        setSearchResults(result.data || []);
        setSearchPerformed(true);
        fetchSearchHistory();
        enqueueSnackbar(
          `Найдено записей: ${result.count || 0}`,
          { variant: 'info' }
        );
      } else {
        enqueueSnackbar(result.message || 'Ошибка поиска', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Ошибка при поиске', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async () => {
    if (!addForm.lastName.trim()) {
      enqueueSnackbar('Фамилия обязательна для заполнения', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const result = await blacklistService.addToBlacklist(addForm);

      if (result.success) {
        enqueueSnackbar('Запись успешно добавлена в черный список', { variant: 'success' });
        setAddDialogOpen(false);
        setAddForm({
          lastName: '',
          firstName: '',
          middleName: '',
          birthDate: '',
          comment: ''
        });
        
        if (searchPerformed) {
          handleSearch();
        }
      } else {
        enqueueSnackbar(result.message || 'Ошибка при добавлении', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Ошибка при добавлении', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedEntry) return;

    setLoading(true);
    try {
      const result = await blacklistService.updateBlacklistEntry(
        selectedEntry._id,
        editForm
      );

      if (result.success) {
        enqueueSnackbar('Запись успешно обновлена', { variant: 'success' });
        setEditDialogOpen(false);
        setSelectedEntry(null);
        
        if (searchPerformed) {
          handleSearch();
        }
      } else {
        enqueueSnackbar(result.message || 'Ошибка при обновлении', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Ошибка при обновлении', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (entry) => {
    setSelectedEntry(entry);
    setEditForm({
      lastName: entry.last_name || '',
      firstName: entry.first_name || '',
      middleName: entry.middle_name || '',
      birthDate: entry.birth_date ? entry.birth_date.split('T')[0] : '',
      comment: entry.comment || ''
    });
    setEditDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Черный список
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <SearchIcon sx={{ mr: 1 }} />
            Поиск в черном списке
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Фамилия</strong> - обязательное поле. <strong>Имя и Отчество</strong> - опциональные.
              Можно использовать сокращения (например, "И" или "И.").
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Фамилия *"
                name="lastName"
                value={searchForm.lastName}
                onChange={handleSearchChange}
                placeholder="Иванов"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Имя"
                name="firstName"
                value={searchForm.firstName}
                onChange={handleSearchChange}
                placeholder="Иван или И"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Отчество"
                name="middleName"
                value={searchForm.middleName}
                onChange={handleSearchChange}
                placeholder="Иванович или И"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={loading || !searchForm.lastName.trim()}
                sx={{ height: '56px' }}
              >
                Поиск
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {searchHistory.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon sx={{ mr: 1 }} />
                История поиска
              </Typography>
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={clearHistory}
                color="error"
              >
                Очистить историю
              </Button>
            </Box>
            <Grid container spacing={1}>
              {searchHistory.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => loadFromHistory(item)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.lastName} {item.firstName} {item.middleName}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.createdAt).toLocaleString('ru-RU')}
                        </Typography>
                        <Chip
                          label={`Найдено: ${item.resultsCount || 0}`}
                          size="small"
                          color={item.resultsCount > 0 ? 'warning' : 'success'}
                          sx={{ height: 20 }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {searchPerformed && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Результаты поиска
              </Typography>
              <Chip 
                label={`Найдено: ${searchResults.length}`} 
                color={searchResults.length > 0 ? 'warning' : 'success'}
                icon={searchResults.length > 0 ? <WarningIcon /> : <InfoIcon />}
              />
            </Box>

            {searchResults.length === 0 ? (
              <Alert severity="success">
                Записей не найдено. Человек не числится в черном списке.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>ФИО</strong></TableCell>
                      <TableCell><strong>Дата рождения</strong></TableCell>
                      <TableCell><strong>Комментарий</strong></TableCell>
                      <TableCell><strong>Добавил</strong></TableCell>
                      <TableCell><strong>Дата добавления</strong></TableCell>
                      <TableCell align="center"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((entry) => (
                      <TableRow 
                        key={entry._id}
                        sx={{ 
                          '&:hover': { bgcolor: 'grey.50' },
                          bgcolor: 'error.50'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.last_name} {entry.first_name} {entry.middle_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(entry.birth_date)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300 }}>
                            {entry.comment || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={entry.merchant_id?.name || 'Неизвестно'} 
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(entry.created_at)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Редактировать">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDialog(entry)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonAddIcon sx={{ mr: 1 }} />
            Добавить в черный список
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Фамилия"
                name="lastName"
                value={addForm.lastName}
                onChange={handleAddChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Имя"
                name="firstName"
                value={addForm.firstName}
                onChange={handleAddChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Отчество"
                name="middleName"
                value={addForm.middleName}
                onChange={handleAddChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Дата рождения"
                name="birthDate"
                value={addForm.birthDate}
                onChange={handleAddChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Комментарий"
                name="comment"
                value={addForm.comment}
                onChange={handleAddChange}
                placeholder="Причина добавления в черный список"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={loading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubmit}
            disabled={loading || !addForm.lastName.trim()}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 1 }} />
            Редактировать запись
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Вы можете редактировать только записи, добавленные вашим мерчантом
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Фамилия"
                name="lastName"
                value={editForm.lastName}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Имя"
                name="firstName"
                value={editForm.firstName}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Отчество"
                name="middleName"
                value={editForm.middleName}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Дата рождения"
                name="birthDate"
                value={editForm.birthDate}
                onChange={handleEditChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Комментарий"
                name="comment"
                value={editForm.comment}
                onChange={handleEditChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blacklist;
